import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { t, useLocale } from '../i18n';
import { IconAlert, IconCheck, IconClose, IconInfo } from './icons';

type ToastKind = 'info' | 'success' | 'error';

interface Toast {
  id: number;
  kind: ToastKind;
  text: string;
}

interface ToastValue {
  push: (text: string, kind?: ToastKind) => void;
  error: (text: string) => void;
  success: (text: string) => void;
}

const ToastContext = createContext<ToastValue | null>(null);

const LIFETIME_MS = 4200;

export function ToastProvider({ children }: { children: ReactNode }) {
  useLocale(); // Sits above App, so it needs its own subscription to re-label the region.
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (text: string, kind: ToastKind = 'info') => {
      const id = nextId.current++;
      setToasts((current) => [...current.slice(-2), { id, kind, text }]);
      setTimeout(() => dismiss(id), LIFETIME_MS);
    },
    [dismiss],
  );

  const value = useMemo<ToastValue>(
    () => ({
      push,
      error: (text: string) => push(text, 'error'),
      success: (text: string) => push(text, 'success'),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toaster" role="region" aria-label={t('ui.notifications')}>
        <div aria-live="polite" aria-atomic="false" className="sr-only">
          {toasts.map((toast) => (
            <p key={toast.id}>{toast.text}</p>
          ))}
        </div>
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast toast--${toast.kind}`}>
            <span className="toast__icon">
              {toast.kind === 'success' ? <IconCheck size={18} /> : toast.kind === 'error' ? <IconAlert size={18} /> : <IconInfo size={18} />}
            </span>
            <span className="toast__text">{toast.text}</span>
            <button type="button" className="icon-btn icon-btn--sm" onClick={() => dismiss(toast.id)} aria-label={t('ui.closeToast')}>
              <IconClose size={16} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside ToastProvider');
  return context;
}
